import { TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';

const CompareKPICard = ({
  metricName,
  unit,
  directionality,
  year1,
  year2,
  value1,
  value2,
  plan1,
  plan2,
  change,
  changePct
}) => {
  const formatValue = (value) => {
    if (value === null || value === undefined) return '—';
    
    if (unit === '$' || unit === '$M' || unit === '$K') {
      if (Math.abs(value) >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (Math.abs(value) >= 1000) {
        return `$${(value / 1000).toFixed(1)}K`;
      }
      return `$${value.toLocaleString()}`;
    }
    
    if (unit === '%') {
      return `${value.toFixed(1)}%`;
    }
    
    if (unit === '#' || !unit) {
      return value.toLocaleString();
    }
    
    return `${value.toLocaleString()} ${unit}`;
  };

  const isPositiveChange = changePct !== null && changePct > 0;
  const isNegativeChange = changePct !== null && changePct < 0;
  const isNoChange = changePct === 0;

  // Determine if change is good or bad based on directionality
  const isGoodChange = directionality === 'lower_is_better' 
    ? isNegativeChange 
    : isPositiveChange;
  const isBadChange = directionality === 'lower_is_better' 
    ? isPositiveChange 
    : isNegativeChange;

  const getChangeColor = () => {
    if (isGoodChange) return 'text-[#059669] bg-[#D1FAE5]';
    if (isBadChange) return 'text-[#DC2626] bg-[#FEE2E2]';
    return 'text-[#6B7280] bg-[#F3F4F6]';
  };

  const getChangeIcon = () => {
    if (isPositiveChange) return <TrendingUp className="w-3 h-3" />;
    if (isNegativeChange) return <TrendingDown className="w-3 h-3" />;
    return <Minus className="w-3 h-3" />;
  };

  return (
    <div 
      className="bg-white rounded-lg border border-[#E5E7EB] p-4 hover:shadow-md transition-shadow"
      data-testid={`compare-card-${metricName.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Metric Name */}
      <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3 truncate">
        {metricName}
      </h3>

      {/* Year Values Side by Side */}
      <div className="flex items-center justify-between gap-2 mb-3">
        {/* Year 1 */}
        <div className="flex-1 text-center">
          <p className="text-xs text-[#9CA3AF] mb-1">{year1}</p>
          <p className="text-lg font-bold text-[#374151]">{formatValue(value1)}</p>
          {plan1 && (
            <p className="text-xs text-[#9CA3AF]">Plan: {formatValue(plan1)}</p>
          )}
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0">
          <ArrowRight className="w-4 h-4 text-[#D1D5DB]" />
        </div>

        {/* Year 2 */}
        <div className="flex-1 text-center">
          <p className="text-xs text-[#9CA3AF] mb-1">{year2}</p>
          <p className="text-lg font-bold text-[#111827]">{formatValue(value2)}</p>
          {plan2 && (
            <p className="text-xs text-[#9CA3AF]">Plan: {formatValue(plan2)}</p>
          )}
        </div>
      </div>

      {/* Change Badge */}
      {changePct !== null && (
        <div className="flex justify-center">
          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getChangeColor()}`}>
            {getChangeIcon()}
            <span>
              {isPositiveChange ? '+' : ''}{(changePct * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      )}

      {/* No Data State */}
      {value1 === null && value2 === null && (
        <div className="text-center text-sm text-[#9CA3AF]">
          No data available
        </div>
      )}
    </div>
  );
};

export default CompareKPICard;
